package com.nonopn.skydome.common.app.pages.estimates

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.Icon
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.rounded.Search
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.estimate_status_active
import com.nonopn.skywatcher.common.app.res.estimate_status_demo
import com.nonopn.skywatcher.common.app.res.estimate_status_empty
import com.nonopn.skywatcher.common.app.res.estimate_status_payment_issue
import com.nonopn.skywatcher.common.app.res.estimate_status_expired
import com.nonopn.skywatcher.common.app.res.estimate_status_external_contract
import com.nonopn.skywatcher.common.app.res.estimate_status_pending_payment
import com.nonopn.skywatcher.common.app.res.estimates_count
import com.nonopn.skywatcher.common.app.res.estimates_filter_all
import com.nonopn.skywatcher.common.app.res.estimates_search_hint
import com.nonopn.skywatcher.design.theme.AppColor
import eu.codlab.compose.widgets.TextNormal
import org.jetbrains.compose.resources.stringResource

/** Search field, status chips and "N of M" counter shown above the list of estimates. */
@Composable
fun EstimatesFilterBar(
    query: String,
    statusFilter: EstimateStatusFilter,
    shown: Int,
    total: Int,
    onChange: (query: String, status: EstimateStatusFilter) -> Unit,
) {
    Column(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 8.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            value = query,
            onValueChange = { onChange(it, statusFilter) },
            placeholder = {
                TextNormal(
                    text = stringResource(Res.string.estimates_search_hint),
                    fontSize = 13.sp,
                    color = Color(0xFF5B6678)
                )
            },
            leadingIcon = {
                Icon(
                    imageVector = Icons.Rounded.Search,
                    contentDescription = null,
                    tint = Color(0xFF5B6678)
                )
            },
            singleLine = true,
            keyboardOptions = KeyboardOptions(imeAction = ImeAction.Search),
            shape = RoundedCornerShape(10.dp),
            colors = OutlinedTextFieldDefaults.colors(
                // Light field with dark text: readable on the dark and on the light themes
                focusedContainerColor = Color.White,
                unfocusedContainerColor = Color.White,
                focusedTextColor = Color(0xFF1A2433),
                unfocusedTextColor = Color(0xFF1A2433),
                cursorColor = Color(0xFF1A2433),
                focusedBorderColor = AppColor.StrikePrimary,
                unfocusedBorderColor = Color(0xFF8A94A6)
            )
        )

        Row(
            modifier = Modifier.horizontalScroll(rememberScrollState()),
            horizontalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            EstimateStatusFilter.values().forEach { status ->
                EstimateChip(
                    label = stringResource(
                        when (status) {
                            EstimateStatusFilter.ALL -> Res.string.estimates_filter_all
                            EstimateStatusFilter.ACTIVE -> Res.string.estimate_status_active
                            EstimateStatusFilter.PENDING_PAYMENT -> Res.string.estimate_status_pending_payment
                            EstimateStatusFilter.EXPIRED -> Res.string.estimate_status_expired
                            EstimateStatusFilter.EXTERNAL_CONTRACT -> Res.string.estimate_status_external_contract
                            EstimateStatusFilter.DEMO -> Res.string.estimate_status_demo
                            EstimateStatusFilter.PAYMENT_ISSUE -> Res.string.estimate_status_payment_issue
                            EstimateStatusFilter.EMPTY -> Res.string.estimate_status_empty
                        }
                    ),
                    selected = statusFilter == status,
                    onClick = { onChange(query, status) }
                )
            }
        }

        // Pill with its own light background so that the counter stays readable on any theme
        Box(
            modifier = Modifier
                .clip(RoundedCornerShape(10.dp))
                .background(Color(0xFFE8EDF5))
                .padding(horizontal = 10.dp, vertical = 3.dp)
        ) {
            TextNormal(
                text = stringResource(Res.string.estimates_count, shown, total),
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                color = Color(0xFF1A2433)
            )
        }
    }
}

@Composable
private fun EstimateChip(label: String, selected: Boolean, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(16.dp))
            .background(if (selected) AppColor.StrikePrimary else Color(0xFFE8EDF5))
            .clickable(onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 5.dp),
    ) {
        TextNormal(
            text = label,
            fontSize = 11.sp,
            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
            color = if (selected) Color.White else Color(0xFF1A2433)
        )
    }
}
